import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Heart, Clock, TrendingUp, LogOut } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { fmtSessionDate, LangSwitch, LogoHeader, PROV, PROV_RSVP_VIDEO, PROV_RSVP_VIDEO_WEBM, provAuth, provClearToken, StickyNav, useProvLang } from "./provShared";

// /provocative/dashboard — persoonlijk member-dashboard (alleen goedgekeurde members).
// 01 NEXT SESSION (dynamisch berekend) · 02 YOUR PROGRESS · 03 WHAT TO BRING · 04 SESSION HISTORY
export default function ProvDashboard() {
  const nav = useNavigate();
  const { lang, setLang, t } = useProvLang();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [gearSel, setGearSel] = useState([]);
  const [gearBusy, setGearBusy] = useState(false);
  const [showVideo, setShowVideo] = useState(false); // bevestigingsvideo-overlay na RSVP
  // WHAT TO EXPECT: standaard open voor nieuwe members; voorkeur bewaard in localStorage
  const [expectOpen, setExpectOpen] = useState(() => localStorage.getItem("prov_expect_open") !== "0");
  const toggleExpect = () => setExpectOpen((o) => {
    const n = !o;
    localStorage.setItem("prov_expect_open", n ? "1" : "0");
    return n;
  });

  const load = () => {
    api.get("/provocative/me/dashboard", provAuth())
      .then(({ data }) => {
        if (data.status !== "approved") nav("/provocative", { replace: true });
        else {
          setData(data);
          setGearSel(data.equipment?.items || []);
        }
      })
      .catch((err) => {
        const s = err?.response?.status;
        if (s === 401 || s === 403 || s === 404) {
          provClearToken();
          nav("/provocative", { replace: true });
        }
      });
  };
  useEffect(load, [nav]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveGear = async () => {
    setGearBusy(true);
    try {
      await api.post("/provocative/me/equipment", { items: gearSel }, provAuth());
      toast.success(t("gear_state_reported"));
      load();
    } catch { toast.error(t("err_generic")); }
    setGearBusy(false);
  };

  const clearGear = async () => {
    setGearBusy(true);
    try {
      await api.delete("/provocative/me/equipment", provAuth());
      setGearSel([]);
      load();
    } catch { toast.error(t("err_generic")); }
    setGearBusy(false);
  };

  const rsvp = async () => {
    setBusy(true);
    try {
      await api.post("/provocative/me/rsvp", { session_date: data.next_session.date }, provAuth());
      setShowVideo(true); // bevestigingsvideo afspelen na geslaagde RSVP
      load();
    } catch { toast.error(t("err_generic")); }
    setBusy(false);
  };

  const cancel = async () => {
    setBusy(true);
    try {
      await api.post("/provocative/me/rsvp/cancel", { session_date: data.next_session.date }, provAuth());
      toast.success(t("history_cancelled"));
      load();
    } catch { toast.error(t("err_generic")); }
    setBusy(false);
  };

  const logout = async () => {
    try { await api.post("/provocative/auth/logout", { token: localStorage.getItem("prov_token") || sessionStorage.getItem("prov_token") }); } catch { /* ignore */ }
    provClearToken();
    nav("/provocative", { replace: true });
  };

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: PROV.bg }} data-testid="prov-dash-loading">
        <span className="font-mono text-[10px] tracking-[0.3em] text-[#666666]">PROVOCATIVE. …</span>
      </div>
    );
  }

  const ns = data.next_session;
  const confirmed = data.my_rsvp === "confirmed";
  const gear = ["gear_gloves", "gear_wraps", "gear_towel", "gear_water", "gear_groin", "gear_bra", "gear_mouthguard"];

  return (
    <div className="min-h-screen" style={{ background: PROV.bg, color: PROV.primary }} data-testid="prov-dashboard">
      <StickyNav>
        <LangSwitch lang={lang} setLang={setLang} />
        <button onClick={logout} className="font-mono text-[10px] tracking-[0.25em] uppercase transition-colors hover:text-[#C1C1C1]" style={{ color: PROV.muted }} data-testid="prov-dash-logout" aria-label={t("logout")}>
          <span className="hidden md:inline">{t("logout")}</span>
          <LogOut size={15} className="md:hidden" aria-hidden="true" />
        </button>
      </StickyNav>
      <header className="flex items-center justify-center px-6 md:px-12 pt-8">
        <LogoHeader compact />
      </header>

      <main className="max-w-2xl mx-auto px-6 py-12 md:py-16 space-y-14">
        {/* 01 — NEXT SESSION */}
        <section data-testid="prov-next-session">
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase" style={{ color: PROV.muted }}>01 — {t("next_session")}</div>
          {ns ? (
            <div className="mt-5 border p-8 text-center" style={{ borderColor: PROV.border }}>
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase" style={{ color: PROV.muted }}>PROVOCATIVE.</div>
              <div className="font-display text-3xl md:text-4xl uppercase mt-4" data-testid="prov-session-date">
                {fmtSessionDate(ns.date, ns.weekday, lang)}
              </div>
              <div className="font-mono text-sm mt-3" style={{ color: PROV.secondary }} data-testid="prov-session-time">
                {ns.start_time} — {ns.end_time}
              </div>
              {ns.location && <div className="font-mono text-[11px] mt-2" style={{ color: PROV.muted }} data-testid="prov-session-location">{ns.location}</div>}
              <div className="mt-8 flex items-center justify-center gap-4 flex-wrap">
                {confirmed ? (
                  <>
                    <span className="px-8 py-4 font-mono text-xs tracking-[0.3em] uppercase border" style={{ borderColor: PROV.highlight, color: PROV.primary }} data-testid="prov-youre-in">
                      □ {t("youre_in")}
                    </span>
                    <button onClick={() => setShowVideo(true)} className="font-mono text-[10px] tracking-[0.25em] uppercase transition-colors hover:text-[#C1C1C1]" style={{ color: PROV.muted }} data-testid="prov-rsvp-video-replay">
                      {t("confirm_replay")}
                    </button>
                    <button onClick={cancel} disabled={busy} className="font-mono text-[10px] tracking-[0.25em] uppercase transition-colors hover:text-[#C1C1C1] disabled:opacity-40" style={{ color: PROV.muted }} data-testid="prov-cancel">
                      {t("cancel_rsvp")}
                    </button>
                  </>
                ) : (
                  <button onClick={rsvp} disabled={busy}
                    className="px-8 py-4 font-mono text-xs tracking-[0.3em] uppercase border transition-colors hover:bg-[#C1C1C1] hover:!text-[#0A0A0A] disabled:opacity-40"
                    style={{ borderColor: PROV.border, color: PROV.primary }} data-testid="prov-im-there">
                    □ {t("im_there")}
                  </button>
                )}
              </div>
            </div>
          ) : (
            <p className="mt-5 text-sm" style={{ color: PROV.muted }} data-testid="prov-no-session">—</p>
          )}
          {data.other_days?.length > 0 && (
            <div className="mt-4 border px-6 py-4" style={{ borderColor: PROV.border }} data-testid="prov-other-days">
              <div className="font-mono text-[10px] tracking-[0.25em] uppercase" style={{ color: PROV.muted }}>{t("also_scheduled")}</div>
              <div className="mt-3 space-y-1.5">
                {data.other_days.map((d) => (
                  <div key={d.date + d.weekday} className="flex justify-between font-mono text-xs" style={{ color: PROV.secondary }} data-testid={`prov-other-day-${d.date}`}>
                    <span>{fmtSessionDate(d.date, d.weekday, lang)}</span>
                    <span>{d.start_time} — {d.end_time}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>

        {/* WHAT TO EXPECT — informatieve onboarding-sectie (klapbaar; staat onthouden).
            Staat tussen de sessie/I'M THERE en de WHAT TO BRING-checklist. */}
        <section data-testid="prov-expect">
          <button onClick={toggleExpect} aria-expanded={expectOpen}
            className="w-full flex items-center justify-between text-left group"
            data-testid="prov-expect-toggle">
            <span className="font-mono text-[10px] tracking-[0.3em] uppercase transition-colors group-hover:text-[#C1C1C1]" style={{ color: PROV.muted }}>
              {t("expect_title")} {expectOpen ? "−" : "+"}
            </span>
          </button>
          {expectOpen && (
            <div className="mt-5 border px-6 py-6" style={{ borderColor: PROV.border }} data-testid="prov-expect-body">
              <p className="font-mono text-xs leading-relaxed whitespace-pre-line" style={{ color: PROV.secondary }} data-testid="prov-expect-intro">
                {t("expect_intro")}
              </p>
              <div className="mt-6 divide-y" style={{ divideColor: PROV.border }}>
                {[
                  ["01", Heart, "expect_1t", "expect_1d"],
                  ["02", Clock, "expect_2t", "expect_2d"],
                  ["03", TrendingUp, "expect_3t", "expect_3d"],
                ].map(([n, Icon, tk, dk]) => (
                  <div key={n} className="py-4 flex gap-4 items-start" data-testid={`prov-expect-${n}`}>
                    <span className="font-mono text-[10px] tracking-[0.25em] mt-0.5 shrink-0" style={{ color: PROV.muted }}>{n}</span>
                    <Icon size={14} className="mt-0.5 shrink-0" style={{ color: PROV.muted }} aria-hidden="true" />
                    <div className="min-w-0">
                      <div className="font-display text-sm uppercase" data-testid={`prov-expect-${n}-title`}>{t(tk)}</div>
                      <div className="font-mono text-[11px] mt-1" style={{ color: PROV.secondary }}>{t(dk)}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="border-t mt-2 pt-6" style={{ borderColor: PROV.border }}>
                <p className="font-display text-lg uppercase text-center" data-testid="prov-expect-closing">{t("expect_closing")}</p>
              </div>
            </div>
          )}
        </section>

        <section data-testid="prov-progress">
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase" style={{ color: PROV.muted }}>02 — {t("your_progress")}</div>
          <div className="mt-5 border px-8 py-6 flex items-baseline gap-4" style={{ borderColor: PROV.border }}>
            <span className="font-mono text-[10px] tracking-[0.25em] uppercase" style={{ color: PROV.secondary }}>{t("sessions_attended")}:</span>
            <span className="font-display text-4xl" data-testid="prov-attended-count">{data.attended_count}</span>
          </div>
        </section>

        {/* 03 — WHAT TO BRING */}
        <section data-testid="prov-gear">
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase" style={{ color: PROV.muted }}>03 — {t("what_to_bring")}</div>
          <ul className="mt-5 border divide-y" style={{ borderColor: PROV.border, divideColor: PROV.border }} data-testid="prov-gear-list">
            {gear.map((g) => {
              const key = g.replace("gear_", "");
              const missing = gearSel.includes(key);
              return (
                <li key={g} className="px-6 py-3.5 font-mono text-xs flex items-center gap-3" style={{ color: missing ? PROV.primary : PROV.secondary }} data-testid={`prov-gear-${g}`}>
                  <button onClick={() => setGearSel((p) => (p.includes(key) ? p.filter((x) => x !== key) : [...p, key]))}
                    className="w-4 h-4 border flex items-center justify-center shrink-0 transition-colors"
                    style={{ borderColor: missing ? PROV.highlight : PROV.border }} aria-label={t(g)} data-testid={`prov-gear-check-${key}`}>
                    {missing && <span className="w-2 h-2" style={{ background: PROV.highlight }} />}
                  </button>
                  {t(g)}
                </li>
              );
            })}
          </ul>
          <div className="mt-4 border px-6 py-4" style={{ borderColor: PROV.border }} data-testid="prov-gear-report">
            <div className="font-mono text-[10px] tracking-[0.25em] uppercase" style={{ color: PROV.muted }}>{t("gear_missing_q")}</div>
            <p className="font-mono text-[11px] mt-2 leading-relaxed" style={{ color: PROV.secondary }}>{t("gear_missing_hint")}</p>
            <div className="mt-3 font-mono text-[10px] tracking-[0.2em] uppercase" style={{ color: PROV.secondary }} data-testid="prov-gear-state">
              {data.equipment ? t(data.equipment.state === "reported" ? "gear_state_reported" : "gear_state_ok") : t("gear_state_none")}
            </div>
            <div className="mt-4 flex gap-4 flex-wrap">
              <button onClick={saveGear} disabled={gearBusy}
                className="px-6 py-3 border font-mono text-[10px] tracking-[0.25em] uppercase transition-colors hover:bg-[#C1C1C1] hover:!text-[#0A0A0A] disabled:opacity-40"
                style={{ borderColor: PROV.border, color: PROV.primary }} data-testid="prov-gear-submit">
                {gearBusy ? "…" : gearSel.length ? t("gear_report") : t("gear_confirm_all")}
              </button>
              {data.equipment && (
                <button onClick={clearGear} disabled={gearBusy} className="font-mono text-[10px] tracking-[0.25em] uppercase transition-colors hover:text-[#C1C1C1] disabled:opacity-40" style={{ color: PROV.muted }} data-testid="prov-gear-clear">
                  {t("gear_clear")}
                </button>
              )}
            </div>
          </div>
        </section>

        {/* 04 — SESSION HISTORY */}
        <section data-testid="prov-history">
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase" style={{ color: PROV.muted }}>04 — {t("session_history")}</div>
          <div className="mt-5 border divide-y" style={{ borderColor: PROV.border, divideColor: PROV.border }} data-testid="prov-history-list">
            {data.history.length === 0 && (
              <div className="px-6 py-4 font-mono text-xs" style={{ color: PROV.muted }} data-testid="prov-history-empty">{t("history_empty")}</div>
            )}
            {data.history.map((h) => (
              <div key={h.id} className="px-6 py-3.5 flex justify-between font-mono text-xs" style={{ color: PROV.secondary }} data-testid={`prov-history-${h.session_date}`}>
                <span>{fmtSessionDate(h.session_date, new Date(`${h.session_date}T00:00:00`).getDay() === 0 ? 6 : new Date(`${h.session_date}T00:00:00`).getDay() - 1, lang)}</span>
                <span style={{ color: h.status === "confirmed" ? PROV.primary : PROV.muted }}>
                  {t(h.status === "confirmed" ? "history_confirmed" : "history_cancelled")}
                </span>
              </div>
            ))}
          </div>
        </section>
      </main>

      {/* bevestigingsvideo na RSVP — fullscreen overlay, sluit automatisch aan het einde */}
      {showVideo && (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center px-6" style={{ background: "rgba(10,10,10,0.97)" }} data-testid="prov-rsvp-video-overlay">
          <button onClick={() => setShowVideo(false)} aria-label={t("confirm_skip")}
            className="absolute top-5 right-5 z-10 w-11 h-11 border flex items-center justify-center transition-colors hover:bg-[#C1C1C1] hover:!text-[#0A0A0A]"
            style={{ borderColor: PROV.border, color: PROV.primary }} data-testid="prov-rsvp-video-skip">
            <X size={18} />
          </button>
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase mb-6" style={{ color: PROV.muted }} data-testid="prov-rsvp-video-title">
            {t("youre_in")}
          </div>
          <video autoPlay controls playsInline onEnded={() => setShowVideo(false)}
            className="w-full max-w-3xl border" style={{ borderColor: PROV.border }} data-testid="prov-rsvp-video">
            <source src={PROV_RSVP_VIDEO} type="video/mp4" />
            <source src={PROV_RSVP_VIDEO_WEBM} type="video/webm" />
          </video>
        </div>
      )}
    </div>
  );
}
